import React, { useState } from 'react';
import { ChevronDown, ArrowRightLeft, Sparkles, GraduationCap, Pencil } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Slider
} from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface StyledButtonsProps {
  onApplyPrompt: (promptText: string) => void;
}

interface PersonaItem {
  icon: string;
  name: string;
}

const StyledButtons: React.FC<StyledButtonsProps> = ({ onApplyPrompt }) => {
  const [sliderValue, setSliderValue] = useState<number[]>([5]);
  const [isPersonaOpen, setIsPersonaOpen] = useState<boolean>(false);
  const [isGradeOpen, setIsGradeOpen] = useState<boolean>(false);

  const handleSliderChange = (value: number[]): void => {
    setSliderValue(value);
  };

  const personas: PersonaItem[] = [
    { icon: "✍️", name: "Direct Response Copywriter" },
    { icon: "🧠", name: "NLP Persuasion" },
    { icon: "🎙️", name: "Podcaster" },
    { icon: "📰", name: "Latest News" },
    { icon: "💜", name: "Companion" },
    { icon: "😃", name: "Comedian" },
    { icon: "👥", name: "Loyal Friend" },
    { icon: "📚", name: "Homework Helper" },
    { icon: "🩺", name: "Not a Doctor" },
    { icon: "🧠", name: "Not a Therapist" },
    { icon: "💬", name: "Text Message" },
    { icon: "📱", name: "Social Post" },
    { icon: "👔", name: "Professional" },
  ];

  const handlePersonaClick = (personaName: string): void => {
    setIsPersonaOpen(false);
    onApplyPrompt(`Acting as an expert ${personaName} rewrite, create or respond to the following: `);
  };

  const handleRewriteClick = (): void => {
    onApplyPrompt("Acting as an expert direct response copywriter Gary Halbert, rewrite: ");
  };

  const handleMakeBetterClick = (): void => {
    onApplyPrompt("Make it better");
  };

  const handleGradeLevelClick = (): void => {
    onApplyPrompt(`Acting as an expert author, rewrite this in a ${sliderValue[0]}th grade level`);
    setIsGradeOpen(false);
  };

  const grades: number[] = [5, 6, 7, 8];

  interface PromptButtonProps {
    onClick: () => void;
    icon: React.FC<{ className?: string }>;
    text: string;
    tooltip?: string;
  }

  const PromptButton: React.FC<PromptButtonProps> = ({ onClick, icon: Icon, text, tooltip }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={onClick}
            variant="outline"
            className="group flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:shadow-sm transition-all h-auto"
          >
            {Icon && <Icon className="h-4 w-4 text-neutral-600 dark:text-neutral-400 group-hover:scale-110 transition-transform" />}
            <span className="text-sm text-neutral-700 dark:text-neutral-300 font-medium">
              {text}
            </span>
          </Button>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent>
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="flex flex-wrap gap-3 justify-center">
      <PromptButton 
        onClick={handleRewriteClick}
        icon={ArrowRightLeft}
        text="Rewrite"
        tooltip="Rewrite with expert copywriting style"
      />
      <PromptButton 
        onClick={handleMakeBetterClick}
        icon={Sparkles}
        text="Improve"
        tooltip="Make your content better"
      />
      <Popover open={isGradeOpen} onOpenChange={setIsGradeOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="group flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:shadow-sm transition-all h-auto"
          >
            <GraduationCap className="h-4 w-4 text-neutral-600 dark:text-neutral-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-neutral-700 dark:text-neutral-300 font-medium">
              Grade Level
            </span>
            <ChevronDown className="h-3 w-3 ml-1 text-neutral-500 dark:text-neutral-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-4 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Select Grade Level</h4>
            <div className="px-1">
              <Slider
                value={sliderValue}
                onValueChange={handleSliderChange}
                max={8}
                min={5}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between mt-2">
                {grades.map((grade) => (
                  <span 
                    key={grade}
                    className={cn(
                      "text-xs",
                      sliderValue[0] === grade 
                        ? "font-bold text-neutral-900 dark:text-neutral-100" 
                        : "text-neutral-500 dark:text-neutral-400"
                    )}
                  >
                    {grade}th
                  </span>
                ))}
              </div>
            </div>
            <Button
              onClick={handleGradeLevelClick}
              variant="outline"
              className="w-full flex items-center justify-center gap-1.5 text-sm font-medium"
            >
              <span>Rewrite in {sliderValue[0]}th Grade Level</span>
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <Popover open={isPersonaOpen} onOpenChange={setIsPersonaOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="group flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:shadow-sm transition-all h-auto"
          >
            <Pencil className="h-4 w-4 text-neutral-600 dark:text-neutral-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-neutral-700 dark:text-neutral-300 font-medium">
              Personas
            </span>
            <ChevronDown className="h-3 w-3 ml-1 text-neutral-500 dark:text-neutral-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2 max-h-80 overflow-y-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
          <div className="grid grid-cols-1 gap-1">
            {personas.map((persona) => (
              <button
                key={persona.name}
                onClick={() => handlePersonaClick(persona.name)}
                className="flex items-center gap-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors duration-150 text-sm p-2 rounded-md text-left text-neutral-700 dark:text-neutral-300"
              >
                <span className="text-base">{persona.icon}</span>
                <span>{persona.name}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default StyledButtons;
